import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { type Event, EventKind } from "../../../api";
import { BadgeZoneViolation } from "@/parts/badge-zone-violation";
import { VehicleZoneViolation } from "@/parts/vehicle-zone-violation";
import { ImpactViolation } from "@/parts/impact-violation";
import { useTranslation } from "react-i18next";
import eloshieldImage from "../../../images/eloshield_zone.png";

export type ViolationReportProps = React.PropsWithChildren & {
    className?: string;
    selectedEvent?: Event;
};

export const ViolationReport = ({
    className,
    selectedEvent,
}: ViolationReportProps): JSX.Element => {
    const { t } = useTranslation();
    if (!selectedEvent) {
        return (
            <Card className={cn("flex flex-col justify-center gap-8 p-8", className)}>
                <img src={eloshieldImage} className="w-full max-w-lg self-center" />
                <div className="text-muted-foreground text-center text-base">
                    {t("component.violationReport.noData")}
                </div>
            </Card>
        );
    }

    function getViolation(): JSX.Element {
        switch (selectedEvent?.kind) {
            case EventKind.BADGE_ZONE_VIOLATION:
                return <BadgeZoneViolation badgeZoneViolationIncidentId={selectedEvent.id} />;
            case EventKind.VEHICLE_ZONE_VIOLATION:
                return <VehicleZoneViolation vehicleZoneViolationIncidentId={selectedEvent.id} />;
            case EventKind.SHOCK:
                return <ImpactViolation shockEventId={selectedEvent.id} />;
            default:
                return <></>;
        }
    }

    const isMuted = selectedEvent.driverMutingEnabled
        ? ` ${t("component.violationReport.isMuted")}`
        : "";

    return (
        <Card className={cn("flex flex-col", className)}>
            <ScrollArea>
                <CardHeader>
                    <CardTitle className="text-xl leading-normal font-bold">
                        {t("component.violationReport.title")}
                    </CardTitle>
                    <CardDescription>
                        {t(`component.violationReport.description.${selectedEvent.kind}`) + isMuted}
                    </CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-6">{getViolation()}</CardContent>
            </ScrollArea>
        </Card>
    );
};
